import { ApiErrorResponse } from "../types/ApiErrorResponse";


export type FetchResponseType = 'json' | 'blob' | 'arraybuffer' | 'text';

export interface FetchRequestConfig<TData = unknown> {
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  responseType?: FetchResponseType;
  signal?: AbortSignal;
  data?: TData;
}

class HttpRequestError extends Error implements ApiErrorResponse {
  code?: string;
  details?: string;
  name: string;
  error?: string;
  response?: {
    data?: any;
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
  };

  constructor(
    message: string,
    code?: string,
    response?: {
      data?: any;
      status?: number;
      statusText?: string;
      headers?: Record<string, string>;
    }
  ) {
    super(message);
    this.name = 'ApiErrorResponse';
    this.code = code;
    this.response = response;
  }
}

const getToken = async (): Promise<string | null> => localStorage.getItem('token');

const toRecordHeaders = (headers: Headers): Record<string, string> => {
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

const appendParams = (url: string, params?: Record<string, unknown>): string => {
  if (!params) {
    return url;
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  if (!queryString) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
};

const parseSuccessResponse = async <TResponse>(
  response: Response,
  responseType: FetchResponseType
): Promise<TResponse> => {
  if (response.status === 204) {
    return undefined as TResponse;
  }

  switch (responseType) {
    case 'blob':
      return (await response.blob()) as TResponse;
    case 'arraybuffer':
      return (await response.arrayBuffer()) as TResponse;
    case 'text':
      return (await response.text()) as TResponse;
    default: {
      const text = await response.text();
      return (text ? JSON.parse(text) : undefined) as TResponse;
    }
  }
};

const parseErrorData = async (response: Response): Promise<any> => {
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    return await response.text();
  } catch {
    return null;
  }
};

const buildBodyAndHeaders = (data: unknown, headers: Record<string, string>) => {
  if (data === undefined || data === null) {
    return { body: undefined, headers };
  }

  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  const isBlob = typeof Blob !== 'undefined' && data instanceof Blob;
  const isArrayBuffer = data instanceof ArrayBuffer;
  const isText = typeof data === 'string';
  const isURLParams = data instanceof URLSearchParams;

  if (isFormData || isBlob || isArrayBuffer || isText || isURLParams) {
    return {
      body: data as BodyInit,
      headers,
    };
  }

  const hasContentType = Object.keys(headers).some((k) => k.toLowerCase() === 'content-type');
  const normalizedHeaders = hasContentType
    ? headers
    : { ...headers, 'Content-Type': 'application/json' };

  return {
    body: JSON.stringify(data),
    headers: normalizedHeaders,
  };
};

const request = async <TResponse, TBody = unknown>(
  method: string,
  url: string,
  data?: TBody,
  config?: FetchRequestConfig<TBody>
): Promise<TResponse> => {
  const token = await getToken();
  const finalUrl = appendParams(url, config?.params);

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(config?.headers ?? {}),
  };

  if (token) {
    requestHeaders.authorization = `Bearer ${token}`;
  }

  const { body, headers } = buildBodyAndHeaders(data, requestHeaders);

  let response: Response;

  try {
    response = await fetch(finalUrl, {
      method,
      headers,
      body,
      signal: config?.signal,
      credentials: 'same-origin',
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Network Error';
    throw new HttpRequestError(message, '0');
  }

  if (!response.ok) {
    const errorData = await parseErrorData(response);
    const backendMessage =
      typeof errorData === 'object' && errorData !== null && typeof errorData.message === 'string'
        ? errorData.message
        : (response.statusText || 'Request failed');

    throw new HttpRequestError(backendMessage, String(response.status), {
      data: errorData,
      status: response.status,
      statusText: response.statusText,
      headers: toRecordHeaders(response.headers),
    });
  }

  return parseSuccessResponse<TResponse>(response, config?.responseType ?? 'json');
};

export const fetchApi = {
  get: <TResponse = unknown>(url: string, config?: FetchRequestConfig) =>
    request<TResponse>('GET', url, undefined, config),
  post: <TResponse = unknown, TBody = unknown>(url: string, data?: TBody, config?: FetchRequestConfig<TBody>) =>
    request<TResponse, TBody>('POST', url, data, config),
  put: <TResponse = unknown, TBody = unknown>(url: string, data?: TBody, config?: FetchRequestConfig<TBody>) =>
    request<TResponse, TBody>('PUT', url, data, config),
  delete: <TResponse = unknown>(url: string, config?: FetchRequestConfig) =>
    request<TResponse>('DELETE', url, config?.data, config),
};

export default fetchApi;
